import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sun, Moon, Menu, X, Lock } from "lucide-react";
import { Logo } from "./Logo";

const ANCHORS = [
  { id: "about", label: "The project" },
  { id: "survey", label: "Survey" },
  { id: "news", label: "Headlines" },
  { id: "voices", label: "Voices" },
  { id: "tables", label: "Tables & stats" },
];

const PAGES = [
  { to: "/big-games", label: "Big games", testid: "nav-link-biggames" },
  { to: "/quiz", label: "Quiz", testid: "nav-link-quiz" },
  { to: "/research", label: "Research", testid: "nav-link-research" },
  { to: "/results", label: "Results", testid: "nav-link-results" },
  { to: "/talk-to-us", label: "Talk to us", testid: "nav-link-talk" },
];

export const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -96 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const Navbar = () => {
  const [dark, setDark] = useState(() => localStorage.getItem("sidelines_theme") === "dark");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("sidelines_theme", dark ? "dark" : "light");
  }, [dark]);

  return (
    <header className="sticky top-0 z-50 px-3 sm:px-6 pt-3 pb-1">
      <div className="max-w-7xl mx-auto nav-pill rounded-full border border-foreground/10 px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5" aria-label="Sidelines home">
          <Logo className="h-9 w-9" />
          <span className="font-display font-bold text-lg tracking-tight">Sidelines</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5">
          {ANCHORS.map((l) => (
            <Link
              key={l.id}
              to={`/#${l.id}`}
              data-testid={`nav-link-${l.id}`}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {l.label}
            </Link>
          ))}
          {PAGES.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className="text-sm font-semibold text-pitch dark:text-turf hover:opacity-75 transition-opacity"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="theme-toggle-btn"
            onClick={() => setDark(!dark)}
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
            className="h-10 w-10 rounded-full border border-foreground/10 flex items-center justify-center hover:bg-muted transition-colors"
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Link
            to="/admin"
            data-testid="admin-login-btn"
            className="hidden sm:inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-pitch text-cream text-sm font-semibold hover:bg-pitch-deep transition-colors dark:bg-turf dark:text-background"
          >
            <Lock className="h-3.5 w-3.5" /> Owner login
          </Link>
          <button
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            className="lg:hidden h-10 w-10 rounded-full border border-foreground/10 flex items-center justify-center"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden max-w-7xl mx-auto mt-2 rounded-3xl nav-pill border border-foreground/10 px-4 py-4">
          <div className="flex flex-col gap-1">
            {ANCHORS.map((l) => (
              <Link
                key={l.id}
                to={`/#${l.id}`}
                onClick={() => setOpen(false)}
                className="px-3 py-2.5 rounded-xl text-sm font-medium hover:bg-muted transition-colors"
              >
                {l.label}
              </Link>
            ))}
            {PAGES.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="px-3 py-2.5 rounded-xl text-sm font-semibold text-pitch dark:text-turf hover:bg-muted transition-colors"
              >
                {l.label}
              </Link>
            ))}
            <Link to="/admin" className="sm:hidden mt-2 inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-pitch text-cream text-sm font-semibold dark:bg-turf dark:text-background">
              <Lock className="h-3.5 w-3.5" /> Owner login
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
